/* Роли сюжета «Рилса» и «Вы в кино» (APP-SPEC-5 §4.4, §8.2; 07-apps §8.4; 03-api §6.3).
   Модель роли — roles2 (V149): min/max, allow_char, chars, self_default, gender. Исполнитель — ссылка
   того же вида, что у остальных рядов «Кто в кадре»: self, user_avatar:ID, person:ID, character:ID.
   Здесь только чистая логика: что подходит роли, куда встаёт касание, чего не хватает. */

export type RoleKind = 'kid' | 'adult' | 'old' | 'pet';
export interface ReelsRole {
  id: string;
  name: string;
  hint: string;
  kinds: RoleKind[];
  min: number;
  max: number;
  allowChar: boolean;
  /** 'all' — весь каталог; массив — id персонажей; строка или число — подборка каталога */
  chars: 'all' | number[] | { collection: string };
  selfDefault: boolean;
  gender: 'm' | 'f' | null;
  /** Набор шаблонов: каким шаблонам нужна роль (подпись группы «Во всех шаблонах» или названия) */
  templates: string[];
  /** Ключ поля формы, куда уходит роль */
  field: string;
}
export interface Performer {
  ref: string;
  name: string;
  image?: string | null;
  type: 'me' | 'avatar' | 'person' | 'char';
  age: Exclude<RoleKind, 'pet'> | null;
  pet: boolean;
  gender: 'm' | 'f' | null;
  ready: boolean;
  /** Подпись строки листа «Люди»: «Муж · взрослый · 4 фото» */
  sub?: string;
  notReady?: string;
  collections?: Array<string | number>;
  /** Запись близкого из «Близких» — для листа «Добавьте фото» */
  person?: import('../../api/people').Person;
}
export type Cast = Record<string, string[]>;

const kindsAll: RoleKind[] = ['kid', 'adult', 'old', 'pet'];
const str = (value: unknown) => (typeof value === 'string' ? value : '');
const int = (value: unknown, fallback: number) => (Number.isInteger(value) && (value as number) >= 0 ? (value as number) : fallback);

/** Роль из ответа сервера (snake_case) или записи прототипа (camelCase); неизвестное — по умолчанию модели. */
export function normalizeRole(raw: unknown, field: string, fallbackName = 'Главный герой'): ReelsRole | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = str(r.id) || str(r.role_id);
  if (!/^[a-zA-Z][a-zA-Z0-9_]{0,31}$/.test(id)) return null;
  const kinds = Array.isArray(r.kinds) ? (r.kinds.filter(k => kindsAll.includes(k as RoleKind)) as RoleKind[]) : [];
  const people = kinds.length ? kinds : (['kid', 'adult', 'old'] as RoleKind[]);
  const adult = people.some(k => k === 'adult' || k === 'old');
  const min = r.min !== undefined ? int(r.min, 1) : r.required === false || r.req === false ? 0 : 1;
  const max = Math.max(1, Math.min(20, int(r.max, 1)), min);
  const gender = r.gender === 'm' || r.gender === 'f' ? r.gender : r.g === 'm' || r.g === 'f' ? r.g : null;
  const chars = r.chars;
  return {
    id,
    field,
    name: str(r.name) || str(r.rn) || fallbackName,
    hint: str(r.hint),
    kinds: people,
    min,
    max,
    allowChar: typeof r.allow_char === 'boolean' ? r.allow_char : typeof r.allowChar === 'boolean' ? r.allowChar : adult,
    chars: Array.isArray(chars)
      ? chars.filter(x => Number.isSafeInteger(x) && x > 0)
      : (typeof chars === 'string' && chars !== 'all') || typeof chars === 'number'
        ? { collection: String(chars) }
        : 'all',
    selfDefault: r.self_default === true || r.me === true,
    gender,
    templates: Array.isArray(r.templates) ? r.templates.filter(x => typeof x === 'string') : [],
  };
}

/** Шаблон без своих ролей играет одной ролью «Главный герой»: себя, близкого или персонажа. */
export function defaultRole(field: string, id = 'me'): ReelsRole {
  return normalizeRole({ id, name: 'Главный герой', min: 1, max: 1, allow_char: true, self_default: true }, field)!;
}

const ageWord: Record<string, string> = { kid: 'ребёнок', adult: 'взрослый', old: 'пожилой', pet: 'питомец' };
/** Подсказка строки «Кто играет»: «можно до 3», «ребёнок», «питомец» */
export function roleTip(role: ReelsRole): string {
  if (role.max > 1) return `можно до ${role.max}`;
  if (role.kinds.length === 1) return ageWord[role.kinds[0]];
  if (role.gender) return role.gender === 'm' ? 'мужская роль' : 'женская роль';
  return '';
}

function accepts(role: ReelsRole, age: RoleKind) {
  return role.kinds.includes(age);
}
function forWhom(role: ReelsRole) {
  if (role.kinds.length === 1 && role.kinds[0] === 'pet') return 'для питомца';
  if (role.kinds.every(k => k === 'kid')) return 'для ребёнка';
  if (role.kinds.every(k => k === 'old')) return 'для пожилого человека';
  return 'для взрослого';
}

/** Почему исполнитель не подходит роли; null — подходит. Неподходящие в ряду бледные, а не скрытые. */
export function ineligible(role: ReelsRole, who: Performer, charNames: (ids: number[]) => string = () => ''): string | null {
  if (who.type === 'char') {
    if (!role.allowChar) return `Роль «${role.name}» ${forWhom(role)}: персонажа сюда не поставить`;
    const id = Number(who.ref.slice('character:'.length));
    if (Array.isArray(role.chars) && !role.chars.includes(id)) {
      const names = charNames(role.chars);
      return names ? `В роль «${role.name}» подходят ${names}` : 'Здесь только персонажи своей подборки';
    }
    if (!Array.isArray(role.chars) && role.chars !== 'all' && !(who.collections ?? []).map(String).includes(role.chars.collection))
      return 'Здесь только персонажи своей подборки';
    if (role.gender && who.gender && who.gender !== role.gender)
      return `Роль «${role.name}» для ${role.gender === 'm' ? 'мужского' : 'женского'} персонажа`;
    return null;
  }
  if (who.pet) return accepts(role, 'pet') ? null : `Роль «${role.name}» ${forWhom(role)}`;
  if (who.type === 'me' || who.type === 'avatar')
    // Пол «Вы» неизвестен (Telegram его не даёт): «Вы» подходит любой роли со взрослыми.
    return accepts(role, 'adult') || accepts(role, 'old') ? null : `Роль «${role.name}» ${forWhom(role)}`;
  if (!accepts(role, who.age ?? 'adult')) return `Роль «${role.name}» ${forWhom(role)}`;
  if (role.gender && who.gender && who.gender !== role.gender) return `Роль «${role.name}» для ${role.gender === 'm' ? 'мужчины' : 'женщины'}`;
  return null;
}

export function roleOf(cast: Cast, ref: string): string | null {
  return Object.keys(cast).find(id => cast[id].includes(ref)) ?? null;
}

/** Первая обязательная роль, где не хватает людей (вход «Выбрать, кто играет», флаг pprole) */
export function firstEmpty(roles: ReelsRole[], cast: Cast, after?: string): string | null {
  const start = after ? roles.findIndex(r => r.id === after) + 1 : 0;
  const order = [...roles.slice(start), ...roles.slice(0, start)];
  return order.find(r => (cast[r.id]?.length ?? 0) < r.min)?.id ?? null;
}

export interface AssignResult {
  cast: Cast;
  focus: string;
  error?: string;
}
/** Касание исполнителя в ряду выбранной роли (APP-SPEC-5 §4.4 «Роли в форме»):
    тот, кто уже в роли, — уходит; у роли на одного — встаёт вместо прежнего (прежний переходит
    на освободившееся место, если подходит); у роли на несколько — отметка до max; выбор переходит
    к следующей пустой обязательной роли. Человек или персонаж играет одну роль. */
export function assign(roles: ReelsRole[], cast: Cast, roleId: string, ref: string, performers: Performer[]): AssignResult {
  const role = roles.find(r => r.id === roleId);
  if (!role) return { cast, focus: roleId };
  const current = cast[roleId] ?? [];
  if (current.includes(ref)) return { cast: { ...cast, [roleId]: current.filter(x => x !== ref) }, focus: roleId };
  if (role.max > 1 && current.length >= role.max) return { cast, focus: roleId, error: `В роли «${role.name}» не больше ${role.max}` };
  const next: Cast = { ...cast };
  const was = roleOf(cast, ref);
  if (was) next[was] = next[was].filter(x => x !== ref);
  if (role.max === 1 && current.length) {
    const displaced = current[0];
    next[roleId] = [ref];
    const back = was ? roles.find(r => r.id === was) : null;
    const who = performers.find(p => p.ref === displaced);
    if (back && who && !ineligible(back, who) && next[back.id].length < back.max) next[back.id] = [...next[back.id], displaced];
  } else next[roleId] = [...current, ref];
  // У роли на несколько выбор остаётся на ней, пока она не полна: детей отмечают подряд.
  const focus = (next[roleId]?.length ?? 0) >= role.max ? (firstEmpty(roles, next, roleId) ?? roleId) : roleId;
  return { cast: next, focus };
}

/** Чего не хватает (подсказка неактивной кнопки, §8.2 «Чего не хватает») */
export function castMissing(roles: ReelsRole[], cast: Cast, performers: Performer[]): string | null {
  for (const role of roles) {
    for (const ref of cast[role.id] ?? []) {
      const who = performers.find(p => p.ref === ref);
      if (!who) return `Выберите, кто играет: ${role.name}`;
      if (!who.ready) return who.notReady ?? (who.type === 'me' ? 'Добавьте свои фото или вариант' : `Добавьте фото: ${who.name}`);
      if (ineligible(role, who)) return `Выберите, кто играет: ${role.name}`;
    }
    if ((cast[role.id]?.length ?? 0) < role.min) return `Выберите, кто играет: ${role.name}`;
  }
  return null;
}

/** «Кто в кадре · N из M» — счёт только по обязательным ролям; без обязательных — «необязательно». */
export function castCount(roles: ReelsRole[], cast: Cast): string {
  const need = roles.filter(r => r.min > 0);
  if (!need.length) return 'необязательно';
  const total = need.reduce((sum, r) => sum + r.min, 0);
  const filled = need.reduce((sum, r) => sum + Math.min(r.min, cast[r.id]?.length ?? 0), 0);
  return `${filled} из ${total}`;
}

/** Состав из входов формы: значения разбираются, дубли и чужие ссылки отбрасываются. */
/** Ключ поля состава в JSON-наборе формы (`screen_fields`, docs/59): `cast: [{role_id, who, avatar_id?, person_id?, templates?}]` (03-api §9.2). */
export const JSON_CAST = 'cast';
const refPattern = /^(self|user_avatar:[1-9][0-9]*|person:[1-9][0-9]*|character:[1-9][0-9]*)$/;
interface CastEntry {
  role_id: string;
  who: 'me' | 'avatar' | 'person' | 'char';
  avatar_id?: number;
  person_id?: number;
  templates?: string[];
}
export function refToEntry(roleId: string, ref: string, templates: string[] = []): CastEntry {
  const [kind, raw] = ref.split(':');
  const id = Number(raw);
  const base: CastEntry =
    kind === 'user_avatar'
      ? { role_id: roleId, who: 'avatar', avatar_id: id }
      : kind === 'person'
        ? { role_id: roleId, who: 'person', person_id: id }
        : kind === 'character'
          ? { role_id: roleId, who: 'char', avatar_id: id }
          : { role_id: roleId, who: 'me' };
  return templates.length ? { ...base, templates } : base;
}
function entryToRef(entry: Record<string, unknown>): string | null {
  const id = (value: unknown) => (Number.isSafeInteger(value) && (value as number) > 0 ? value : null);
  if (entry.who === 'me') return 'self';
  if (entry.who === 'avatar' && id(entry.avatar_id)) return `user_avatar:${entry.avatar_id}`;
  if (entry.who === 'person' && id(entry.person_id)) return `person:${entry.person_id}`;
  if (entry.who === 'char' && id(entry.avatar_id)) return `character:${entry.avatar_id}`;
  return null;
}
const json = (roles: ReelsRole[]) => roles.length > 0 && roles.every(role => role.field === JSON_CAST);

/** Состав из входов формы: значения разбираются, дубли и чужие ссылки отбрасываются.
    Строковые поля `cast_<роль>` — ссылки через запятую; JSON-поле `cast` — плоский список 03-api §9.2. */
export function castFromInputs(roles: ReelsRole[], inputs: Record<string, string>): Cast {
  const seen = new Set<string>();
  const out: Cast = {};
  let entries: Array<Record<string, unknown>> = [];
  if (json(roles))
    try {
      const parsed: unknown = JSON.parse(inputs[JSON_CAST] ?? '[]');
      entries = Array.isArray(parsed) ? parsed.filter(x => !!x && typeof x === 'object') : [];
    } catch {
      entries = [];
    }
  for (const role of roles) {
    const refs = json(roles)
      ? entries.filter(entry => entry.role_id === role.id).map(entryToRef)
      : (inputs[role.field] ?? '').split(',').map(x => x.trim());
    out[role.id] = [];
    // Лишние сверх max не занимают человека: он может играть другую роль.
    for (const ref of refs)
      if (ref && refPattern.test(ref) && !seen.has(ref) && out[role.id].length < role.max) {
        seen.add(ref);
        out[role.id].push(ref);
      }
  }
  return out;
}
export function castToInputs(roles: ReelsRole[], cast: Cast, inputs: Record<string, string>): Record<string, string> {
  const next = { ...inputs };
  if (json(roles)) {
    const entries = roles.flatMap(role => (cast[role.id] ?? []).map(ref => refToEntry(role.id, ref, role.templates.length > 1 ? role.templates : [])));
    if (entries.length) next[JSON_CAST] = JSON.stringify(entries);
    else delete next[JSON_CAST];
    return next;
  }
  for (const role of roles) {
    const value = (cast[role.id] ?? []).join(',');
    if (value) next[role.field] = value;
    else delete next[role.field];
  }
  return next;
}

/** Состав после загрузки людей: только те, кто есть в ряду и подходит роли; «Вы» встаёт в первую
    роль self_default без пола (роль с полом сама никем не занимается). */
export function fitCast(roles: ReelsRole[], cast: Cast, performers: Performer[], seedSelf: boolean): Cast {
  const out: Cast = {};
  for (const role of roles)
    out[role.id] = (cast[role.id] ?? []).filter(ref => {
      const who = performers.find(p => p.ref === ref);
      return !!who && !ineligible(role, who);
    });
  const me = performers.find(p => p.type === 'me' || p.type === 'avatar');
  if (seedSelf && me?.ready && !roleOf(out, me.ref)) {
    const target = roles.find(r => r.selfDefault && !r.gender && (out[r.id]?.length ?? 0) < r.max && !ineligible(r, me));
    if (target) out[target.id] = [...(out[target.id] ?? []), me.ref];
  }
  return out;
}

/** Показывать ли карточки ролей: ролей больше одной или одна роль на несколько человек */
export const roleCards = (roles: ReelsRole[]) => roles.length > 1 || roles.some(r => r.max > 1);

/** Кто в роли словами («Вы и Андрей») — строка карточки хвоста */
export function castWords(roles: ReelsRole[], cast: Cast, performers: Performer[]): string {
  const names = roles.flatMap(r => cast[r.id] ?? []).map(ref => {
    const who = performers.find(p => p.ref === ref);
    return who?.type === 'me' || who?.type === 'avatar' ? 'Вы' : (who?.name ?? '');
  }).filter(Boolean);
  if (names.length < 3) return names.join(' и ');
  return `${names.slice(0, 2).join(', ')} и ещё ${names.length - 2}`;
}
